import {
  Alert,
  AlertTitle,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ConfigAndMapDetailsTable from "./ConfigAndMapDetailsTable";

type StepKey = "fetcher" | "unifier" | "advisor";

interface Props {
  open: boolean;
  handleClose: () => void;
  stepKey: StepKey;
  interfaceName: string;
  thing: any;
}

const tableHeadings = {
  metric: "Metric",
  derivation: "Derivation",
};

const ADVISOR_METRICS = [
  "ftd",
  "qftd",
  "cpa_income",
  "revshare_income",
  "net_revenue",
  "total_income",
];

function pluralize(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function FieldsGrid({
  items,
  sx,
}: {
  items: { label: string; value: any }[];
  sx?: object;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "2fr 1.8fr 5fr",
        gap: 3,
        py: 2,
        ...sx,
      }}
    >
      {items.map((item) => (
        <Box key={item.label}>
          <Typography sx={{ fontSize: "12px", color: "#00000099", mb: 0.2 }}>
            {item.label}
          </Typography>
          <Typography sx={{ fontSize: "16px", fontWeight: 400, color: "#000000DE" }}>
            {item.value ?? "N/A"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function ValidationAlert({ children }: { children: React.ReactNode }) {
  return (
    <Alert
      icon={<InfoOutlinedIcon />}
      severity="info"
      sx={{
        mt: 1,
        border: "1px solid #0288D180",
        borderRadius: "5px",
        backgroundColor: "#f4faff",
        color: "#0288D10A",
        alignItems: "flex-start",
        p: 1,

        "& .MuiAlert-icon": {
          color: "#0066a6",
          fontSize: "20px",
          p: 0,
        },

        "& .MuiAlert-message": {
          width: "100%",
          p: 0,
        },
      }}
    >
      <AlertTitle
        sx={{
          fontSize: "14px",
          fontWeight: 500,
          letterSpacing: "0.5px",
          color: "#01579B",
          mb: 0,
        }}
      >
        VALIDATION
      </AlertTitle>

      <Typography sx={{ fontSize: "14px", color: "#01579B" }}>
        {children}
      </Typography>
    </Alert>
  );
}

const ConfigAndMapDetailsModal = ({
  open,
  handleClose,
  stepKey,
  interfaceName,
  thing,
}: Props) => {
  if (!thing) return null;

  const name = thing.configuration_name ?? thing.map_name;
  const id = thing.config_id ?? thing.map_id;
  const stepTitle = stepKey.charAt(0).toUpperCase() + stepKey.slice(1);

  // metrics is a fixed object of {metric: gap-rule value}; 0 means no gap rule
  // exists, so the metric passes through from the Unifier map unchanged.
  // Iterate the known metric keys (rather than Object.entries) so a metric
  // the API omits still shows its "taken from Unifier" placeholder row.
  const metricsRows = ADVISOR_METRICS.map((metric) => {
    const value = thing.metrics?.[metric];
    return {
      metric,
      derivation:
        value === 0 || value == null
          ? "as delivered from unifier"
          : String(value),
    };
  });

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{ sx: { width: "650px", maxWidth: "650px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <DialogTitle sx={{ padding: "0px 0px 10px 0px" }}>
                {name}{" "}
                <Box
                  component="span"
                  sx={{ color: "#00000099", fontWeight: 500 }}
                >
                  ({id})
                </Box>{" "}
                <Box
                  component="span"
                  sx={{ color: "#00000061", fontWeight: 400 }}
                >
                  {stepTitle} {stepKey === "fetcher" ? "config" : "map"} details
                </Box>
              </DialogTitle>
              <Typography variant="body2" color="#00000061">
                Used By {pluralize(thing.used_by_brands ?? 0, "brand", "brands")}
                {stepKey === "fetcher" && thing.format
                  ? ` ● ${thing.format} Format`
                  : ""}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {stepKey === "fetcher" && (
            <>
              <FieldsGrid
                items={[
                  { label: "Interface", value: interfaceName },
                  { label: "Report", value: thing.report_name },
                  { label: "Collection method", value: thing.collection_method },
                  { label: "Collection Type", value: thing.collection_type },
                  { label: "Sub-filtering", value: thing.sub_filtering_method },
                ]}
              />
              <ValidationAlert>
                Connecting a brand runs a live collection test through Argo
                (via the Connect Manager) before the connection is activated.
              </ValidationAlert>
            </>
          )}

          {stepKey === "unifier" && (
            <>
              <FieldsGrid
                sx={{ borderBottom: "1px solid #e0e0e0" }}
                items={[
                  { label: "Interface", value: interfaceName },
                  { label: "Destination Table", value: thing.destination_table },
                ]}
              />
              <Box sx={{ mt: 2 }}>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#000000DE",
                    mb: 1,
                  }}
                >
                  Filtering Definition
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {/* thing.filtering_columns isn't populated by the Families
                      overview API yet — the real data (unique_definition keys)
                      lives per field-mapping row behind data_unique_definitions
                      and needs its own fetch to wire up. Renders empty until then. */}
                  {(thing.filtering_columns ?? []).map((column: string) => (
                    <Chip
                      key={column}
                      label={column}
                      variant="outlined"
                      sx={{
                        height: "28px",
                        borderRadius: "16px",
                        borderColor: "#d9d9d9",
                        backgroundColor: "#fff",
                        fontSize: "14px",
                        color: "#000000DE",
                        px: 0.8,

                        "& .MuiChip-label": {
                          px: 0.2,
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <ValidationAlert>
                Connecting a brand verifies every required column exists on
                the brand&apos;s raw_data table. Missing columns block the
                connection.
              </ValidationAlert>
            </>
          )}

          {stepKey === "advisor" && (
            <>
              <FieldsGrid
                sx={{ borderBottom: "1px solid #e0e0e0" }}
                items={[
                  { label: "Interface", value: interfaceName },
                  { label: "Destination Table", value: thing.destination_table },
                ]}
              />
              <ConfigAndMapDetailsTable
                tableHeadings={tableHeadings}
                rows={metricsRows}
              />
              <ValidationAlert>
                Metric dependency validation was performed when this map was
                created — connecting a brand requires no further validation.
              </ValidationAlert>
            </>
          )}

          <Stack flexDirection={"row"} justifyContent={"end"} marginTop={2}>
            <Button variant="outlined" color="secondary" onClick={handleClose}>
              Close
            </Button>
          </Stack>
        </Paper>
      </Dialog>
    </>
  );
};

export default ConfigAndMapDetailsModal;
